import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { Button } from "@accredo/fe-shared/theme/components";
import { useBankAccountActions } from "hooks/useBankAccountActions.jsx";

/**
 * A customer has at most one default account, so setting a new one always
 * replaces whatever was set before. Fetches the customer's own accounts fresh
 * (rather than trusting what happens to be loaded in the table) to find the
 * current default reliably, regardless of pagination or which page of the
 * table triggered this.
 */
export default function SetDefaultAccountDialog({
  open,
  bankAccount,
  onClose,
  onConfirmed,
}) {
  const { setCustomerDefault, fetchCustomerBankAccounts } =
    useBankAccountActions();

  // undefined while loading, null once loaded if there is no current default.
  const [currentDefault, setCurrentDefault] = useState(undefined);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !bankAccount) return;

    setCurrentDefault(undefined);
    fetchCustomerBankAccounts(bankAccount.kunden_id, (res) => {
      const accounts = res?.data ?? [];
      setCurrentDefault(
        accounts.find(
          (account) =>
            account.is_customer_default && account.id !== bankAccount.id
        ) ?? null
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bankAccount?.id]);

  const handleClose = () => {
    setCurrentDefault(undefined);
    onClose();
  };

  const handleConfirm = async () => {
    setIsSaving(true);
    const result = await setCustomerDefault(bankAccount.id, true);
    setIsSaving(false);

    if (result) {
      onConfirmed?.();
      handleClose();
    }
  };

  const isLoading = currentDefault === undefined;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Standardkonto setzen</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Wird geladen…
          </Typography>
        ) : currentDefault ? (
          <Typography variant="body2">
            Aktuelles Standardkonto ist <strong>{currentDefault.iban}</strong>{" "}
            ({currentDefault.bank_name}). Soll es durch{" "}
            <strong>{bankAccount.iban}</strong> ersetzt werden?
          </Typography>
        ) : (
          <Typography variant="body2">
            Soll <strong>{bankAccount.iban}</strong> als Standardkonto für
            Schadenzahlungen festgelegt werden?
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="tertiary" onClick={handleClose}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isLoading || isSaving}
        >
          {currentDefault ? "Ersetzen" : "Festlegen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SetDefaultAccountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  bankAccount: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    iban: PropTypes.string,
    kunden_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onClose: PropTypes.func.isRequired,
  onConfirmed: PropTypes.func,
};
